import { Component, ViewChild } from "@angular/core";
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";
import { Nav, Platform } from "ionic-angular";
import moment from "moment";
import { Observable, Observer, Subscription } from "rxjs";
import { timer } from "rxjs/observable/timer";

import { Autostart } from "@ionic-native/autostart";
import { BackgroundMode } from "@ionic-native/background-mode";
import { Deeplinks } from "@ionic-native/deeplinks";
import { NativeAudio } from "@ionic-native/native-audio";
import { SplashScreen } from "@ionic-native/splash-screen";
import { StatusBar } from "@ionic-native/status-bar";

import { environment } from "../environments/environment";
import { MainPage } from "../pages";

import { Settings, SocketService, Constants } from "../providers";
import { AuthService } from "../providers/api/auth.service";
import { FirstRunPage } from "../pages/index";
import { PanicProvider } from "../providers/panic/panic";
import { SmokeProvider } from "../providers/smoke/smoke";
import { NodeAvailabilityProvider } from "../providers/node-availability/node-availability";
import { DashboardPage } from "../pages/dashboard/dashboard";
import { CacheService } from "ionic-cache";
import { DoorBellProvider } from "../providers/door-bell/door-bell";

@Component({
  templateUrl: "app.html",
})
export class MyApp {
  private tokenSubscriptionInterval: number = 24 * 60 * 60 * 1000; // A day interval ( 24 * 60 * 60 * 1000)

  public TYPE_ENDPOINTS = Constants.TYPE_ENDPOINTS;
  public TYPE_NODES = Constants.TYPE_NODE;

  private tokenSubscription$: Subscription;
  private connectSubscription$: Subscription;
  private disconnectSubscription$: Subscription;
  private ioMeasureCreated$: Subscription;
  private ioActuationUpdated$: Subscription;

  public showSplash = true;
  public env = environment;
  private mainPage = MainPage;
  private audios = [
    { key: "CARILLON_AUDIO_KEY", file: "assets/carillon.mp3" },
    { key: "DEFAULT_AUDIO_KEY", file: "assets/default.mp3" },
    { key: "DINGDONG_AUDIO_KEY", file: "assets/ding_dong.mp3" },
    { key: "OLD_FASHIONED_AUDIO_KEY", file: "assets/old_fashioned.mp3" },
    { key: "STANDARD_AUDIO_KEY", file: "assets/standard.mp3" },
    { key: "PANIC_AUDIO_KEY", file: "assets/panic.mp3" },
    { key: "SMOKE_AUDIO_KEY", file: "assets/smoke.mp3" },
  ];

  @ViewChild(Nav) nav: Nav;

  constructor(
    private translate: TranslateService,
    private autoStart: Autostart,
    private statusBar: StatusBar,
    private splashScreen: SplashScreen,
    private socketService: SocketService,
    private auth: AuthService,
    private backgroundMode: BackgroundMode,
    private deeplinks: Deeplinks,
    private nativeAudio: NativeAudio,
    private availability: NodeAvailabilityProvider,
    private panicProvider: PanicProvider,
    private smokeProvider: SmokeProvider,
    private doorBellProvider: DoorBellProvider,
    private cache: CacheService,
    public platform: Platform,
    public settings: Settings
  ) {
    console.log("ENVIRONMENT", environment);

    platform.ready().then(() => {
      this.cache.setDefaultTTL(60 * 4);

      if (platform.is("cordova")) {
        // Okay, so the platform is ready and our plugins are available.
        // Here you can do any higher level native things you might need.
        this.statusBar.hide();
        this.statusBar.overlaysWebView(true);

        if (platform.is("android")) {
          this.autoStart.enable();
        }
        this.splashScreen.hide();

        this.setDeeplinks();

        if (!this.backgroundMode.isEnabled()) {
          this.backgroundMode.enable();

          this.backgroundMode.disableWebViewOptimizations();
        }

        this.backgroundMode.on("activate").subscribe(() => {
          if (this.tokenSubscription$) {
            this.tokenSubscription$.unsubscribe();
          }
          this.backgroundTokenSubscription();

          // this.nativeAudio.stop("RING_AUDIO_KEY");
          // this.nativeAudio.stop("PANIC_AUDIO_KEY");
          // this.nativeAudio.stop("SMOKE_AUDIO_KEY");
        });

        this.backgroundMode.on("deactivate").subscribe(() => {
          if (this.tokenSubscription$) {
            this.tokenSubscription$.unsubscribe();
          }
          this.foregroundTokenSubscription();
        });

        this.preloadAudios();

        // Prevent Back Button
        platform.registerBackButtonAction(() => {});
      }

      timer(3000).subscribe(() => (this.showSplash = false));

      Observable.of(this.nav.viewDidLoad)
        .flatMap((res) => {
          return this.checkForToken();
        })
        .subscribe(
          (hasStayLogged) => {
            if (hasStayLogged) {
              console.error("hasStayLogged");

              this.socketService.initSocket();

              this.foregroundTokenSubscription();
              this.nav.setRoot(this.mainPage);
            } else if (!hasStayLogged) {
              console.error("!hasStayLogged");
              // Here I should carry out the loading screen
              if (!this.auth.hasValidToken()) {
                this.nav.setRoot(FirstRunPage);
                //this.auth.logout();
              } else {
                this.nav.setRoot(this.mainPage);
                this.foregroundTokenSubscription();
              }
            }
          },
          (err) => {
            console.error("hasStayLogged err", err);
            // Here I should carry out the loading screen
            this.nav.setRoot(this.mainPage);

            this.foregroundTokenSubscription();
          }
        );
    });

    // Set some default door bell audio
    this.settings.storage.keys().then((values) => {
      if (!values.includes("doorbellTone")) {
        this.settings.updateDoorbellTone("DEFAULT_AUDIO_KEY");
      }
    });

    this.initTranslate();
    this.socketService.isSocketConnected().subscribe((res: any) => {
      if (res) {
        this.availability.checkForNodeAvailability();

        this.ioSubscribe();
      } else {
        this.ioUnsubscribe();
      }
    });

    this.auth.goToPageInitListener().subscribe((res) => {
      console.log("goToPageInitListener");
      this.nav.setRoot(this.nav.getActive().component.name);
    });
  }

  preloadAudios() {
    for (let audio of this.audios) {
      this.nativeAudio
        .preloadComplex(audio.key, audio.file, 1, 1, 0)
        .then(this._loadAudioSuccess, this._loadAudioError);
    }
  }

  _loadAudioSuccess() {
    console.log("sonido :::> cargado");
  }

  _loadAudioError() {
    //console.log('sonido :::> cargado');
  }

  setDeeplinks() {
    this.deeplinks
      .route({
        "/": DashboardPage,
      })
      .subscribe(
        (match) => {
          // match.$route - the route we matched, which is the matched entry from the arguments to route()
          // match.$args - the args passed in the link
          // match.$link - the full link data
          console.log("Successfully matched route", match);
        },
        (nomatch) => {
          // nomatch.$link - the full link data
          console.error("Got a deeplink that didn't match", nomatch);
        }
      );
  }

  backgroundTokenSubscription() {
    if (this.auth.hasToken()) {
      // Set interval to half hour
      let subscriptionInterval = this.tokenSubscriptionInterval / 48;

      this.tokenSubscription(subscriptionInterval);
    }
  }

  foregroundTokenSubscription() {
    if (this.auth.hasToken()) {
      // Set interval to 1 hour
      let subscriptionInterval = this.tokenSubscriptionInterval / 24;

      this.tokenSubscription(subscriptionInterval);
    }
  }

  tokenSubscription(subscriptionInterval) {
    this.tokenSubscription$ = Observable.interval(subscriptionInterval)
      .flatMap((res) => {
        return this.checkForToken();
      })
      .subscribe();
  }

  //Check Localstorage token and compare
  checkForToken() {
    console.log("checkForToken");
    if (!this.auth.hasValidToken()) {
      return Observable.create((observable: Observer<any>) => {
        this.auth.rememberDoLogin().subscribe(
          (res) => {
            if (res.success) {
              this.socketService.initSocket();

              observable.next(res.success);
            } else {
              observable.next(false);
            }
            observable.complete();
          },
          (err) => {
            observable.next(false);
            observable.complete();
          }
        );
      });
    }

    return Observable.create((observable) => {
      observable.next(true);
      observable.complete();
    });
  }

  initTranslate() {
    // Set the default language for translation strings, and the current language.
    this.translate.addLangs(["en", "de", "fr", "es", "it"]);
    // this.translate.setDefaultLang("en");
    // moment.locale("en");

    const browserLang = this.translate.getBrowserLang();
    if (browserLang && !this.settings.getCurrentLanguage()) {
      if (browserLang === "zh") {
        const browserCultureLang = this.translate.getBrowserCultureLang();

        if (browserCultureLang.match(/-CN|CHS|Hans/i)) {
          this.translate.use("zh-cmn-Hans");
        } else if (browserCultureLang.match(/-TW|CHT|Hant/i)) {
          this.translate.use("zh-cmn-Hant");
        }
      } else {
        moment.locale(browserLang);
        this.translate.use(browserLang);
      }
    } else {
      if (this.settings.getCurrentLanguage()) {
        this.translate.setDefaultLang(this.settings.getCurrentLanguage());
        moment.locale(this.settings.getCurrentLanguage());
      } else {
        this.translate.setDefaultLang("en");
        moment.locale("en");
      }
    }

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.translate.reloadLang(event.lang);
    });
  }

  openPage(page) {
    // Reset the content nav to have just this page
    // we wouldn't want the back button to show in this scenario
    this.nav.setRoot(page.component);
  }

  ioSubscribe() {
    this.ioUnsubscribe();

    this.ioMeasureCreated$ = this.socketService
      .onMeasureCreated()
      .subscribe((measure: any) => {
        if (this.availability.myItems.indexOf(measure.device) !== -1) {
          switch (measure.uid.code) {
            case this.TYPE_NODES.DOOR_BELL:
            case this.TYPE_NODES.SMART_BELL:
            case this.TYPE_NODES.SMART_SIMPLE:
            case this.TYPE_NODES.SMART_BELL_LP:
              // TODO: Re-comment when WebSocket MQTT is tested and being used
              this.availability
                .checkOnMyDevices(this.availability.doorbellItems, measure)
                .subscribe((endpointFound) => {
                  if (endpointFound) {
                    if (
                      measure.id === this.TYPE_ENDPOINTS.RELAY_0 &&
                      measure.value === "On"
                    ) {
                      this.doorBellProvider.activateDoorBell(measure);
                    }
                  }
                });
              break;
            case this.TYPE_NODES.CLIMA:
              this.availability
                .checkOnMyDevices(this.availability.panicItems, measure)
                .subscribe((endpointFound) => {
                  if (endpointFound) {
                    if (
                      measure.id === this.TYPE_ENDPOINTS.RELAY_0 &&
                      measure.value === "On"
                    ) {
                      this.panicProvider.activatePanic(measure);
                    }
                  }
                });
              break;
            case this.TYPE_NODES.DISPLAY_WIFI:
              this.availability
                .checkOnMyDevices(this.availability.smokeItems, measure)
                .subscribe((endpointFound) => {
                  if (endpointFound) {
                    if (
                      measure.id === this.TYPE_ENDPOINTS.RELAY_0 &&
                      measure.value === "On"
                    ) {
                      this.smokeProvider.activateSmoke(measure);
                    }
                  }
                });
              break;
            default:
              switch (measure.id) {
                case this.TYPE_ENDPOINTS.IP:
                case this.TYPE_ENDPOINTS.RSSI:
                case this.TYPE_ENDPOINTS.UPTIME:
                case this.TYPE_ENDPOINTS.UPTIME2:
                case this.TYPE_ENDPOINTS.VERSION:
                case this.TYPE_ENDPOINTS.VCC:
                  break;
                default:
                  if (this.platform.is("cordova")) {
                    if (this.backgroundMode.isActive()) {
                      this.availability.filterMeasureNotifications(measure);
                    }
                  }
              }
          }
        }
      });

    this.ioActuationUpdated$ = this.socketService
      .onActuationUpdated()
      .subscribe((actuation: any) => {
        this.cache.clearAll();
        if (this.availability.myItems.indexOf(actuation.device) !== -1) {
          switch (actuation.id) {
            case this.TYPE_ENDPOINTS.IP:
            case this.TYPE_ENDPOINTS.RSSI:
            case this.TYPE_ENDPOINTS.UPTIME:
            case this.TYPE_ENDPOINTS.UPTIME2:
            case this.TYPE_ENDPOINTS.VERSION:
            case this.TYPE_ENDPOINTS.VCC:
              break;
            default:
              if (this.platform.is("cordova")) {
                if (this.backgroundMode.isActive()) {
                  this.availability.filterActuationNotifications(actuation);
                }
              }
          }
        }
      });
  }

  ioUnsubscribe() {
    if (this.ioMeasureCreated$) {
      this.ioMeasureCreated$.unsubscribe();
    }
    if (this.ioActuationUpdated$) {
      this.ioActuationUpdated$.unsubscribe();
    }
  }

  ngOnDestroy() {
    if (this.tokenSubscription$) {
      this.tokenSubscription$.unsubscribe();
    }
    if (this.connectSubscription$) {
      this.connectSubscription$.unsubscribe();
    }
    if (this.disconnectSubscription$) {
      this.disconnectSubscription$.unsubscribe();
    }

    this.ioUnsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts